import {updateUrlHash} from '@github-ui/history'
import {MaximizeIcon, XIcon} from '@primer/octicons-react'
import {AnchoredOverlay, Dialog, Heading, IconButton, Spinner, Stack} from '@primer/react'
import {useCallback, useMemo, useRef, useState, type RefObject} from 'react'

import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import type {PullRequest} from '../../page-data/payloads/toolbar'
import {isPullRequestOpenState} from '../../utils/pull-request-state'
import {useSubmitReviewMutation, ReviewEvent} from '../../mutations/use-submit-review-mutation'
import type {ReviewResponse} from '../../page-data/payloads/review-response'
import {useAbandonReviewMutation} from '../../mutations/use-abandon-review-mutation'
import {usePersistedReview} from '../../hooks/use-persisted-review-data'
import styles from './ReviewMenu.module.css'

import {usePendingReviews, usePullRequestBasePath} from '../../stores/selectors'
import {OverlayPortalContainer} from '../toolbar/OverlayPortalContainer'
import {useReviewMenuData} from '../../page-data/loaders/use-review-menu-data'
import {ReviewMenuContent} from './ReviewMenuContent'
import {ReviewMenuFooter} from './ReviewMenuFooter'
import {getAllowedReviewMode} from './get-allowed-review-mode'

const REVIEW_MENU_PORTAL_ROOT = 'review-menu-portal-root'

/** Pull request object without deferred data fields. If these fields aren't present, the component will fetch its own data. */
type UnenrichedPullRequest = Omit<
  PullRequest,
  'comparison' | 'repository' | 'id' | 'viewerIsCopilotAttributed' | 'viewerHasViolatedPushPolicy'
>

const isEnrichedPullRequest = (pr: PullRequest | UnenrichedPullRequest): pr is PullRequest =>
  'viewerIsCopilotAttributed' in pr &&
  pr.viewerIsCopilotAttributed !== undefined &&
  'viewerHasViolatedPushPolicy' in pr &&
  pr.viewerHasViolatedPushPolicy !== undefined

export type ReviewMenuOverlayMode = 'closed' | 'dialog' | 'anchored'

export interface ReviewMenuProps {
  currentUserLogin: string
  pullRequest: PullRequest | UnenrichedPullRequest
  headRefOid: string
  repository: {id?: number; viewerPermission?: string}
  redirectOnMutation?: boolean
  /** Controls where the DOM for the overlay renders (into the header, or at the global default portal root). */
  overlayPortal?: 'internal' | 'external'
  /** Base branch name from stacked PRs, used to clarify the approve sub-label. */
  stackBaseBranch?: string
  overlayMode: ReviewMenuOverlayMode
  onOverlayModeChange: (open: ReviewMenuOverlayMode) => void
  anchorRef: RefObject<HTMLElement | null>
}

const SCROLL_TO_ELEMENT_TIMEOUT_MS = 10_000

/**
 * Scrolls to and focuses a DOM element by ID, waiting for it to appear if it's not yet in the DOM.
 * The review timeline item arrives via live update and may not exist immediately.
 */
function scrollToElementWhenAvailable(elementId: string) {
  const existing = document.getElementById(elementId)
  if (existing) {
    focusAndScrollTo(existing)
    return
  }

  const timeoutId = setTimeout(() => observer.disconnect(), SCROLL_TO_ELEMENT_TIMEOUT_MS)

  const observer = new MutationObserver(() => {
    const el = document.getElementById(elementId)
    if (el) {
      clearTimeout(timeoutId)
      observer.disconnect()
      focusAndScrollTo(el)
    }
  })

  observer.observe(document.body, {childList: true, subtree: true})
}

function focusAndScrollTo(el: HTMLElement) {
  // Ensure the element is focusable — timeline items are typically non-interactive
  // containers, so we need tabIndex to allow programmatic focus.
  if (!el.hasAttribute('tabindex')) {
    el.setAttribute('tabindex', '-1')
  }
  el.focus()
  el.scrollIntoView()
}

const defaultDeferredData = {
  viewerHasViolatedPushPolicy: false,
  viewerIsCopilotAttributed: false,
  viewerPermission: 'WRITE',
}

export function ReviewMenu({
  currentUserLogin,
  pullRequest,
  headRefOid,
  repository,
  redirectOnMutation = true,
  overlayPortal = 'internal',
  stackBaseBranch,
  overlayMode,
  onOverlayModeChange,
  anchorRef,
}: ReviewMenuProps) {
  const pathName = usePullRequestBasePath()

  const {author, state, viewerAllowedNonCommentReviewTypes} = pullRequest

  const pendingCommentData = usePendingReviews()

  const deferredDataEnabled = !isEnrichedPullRequest(pullRequest)

  const {data: deferredData = defaultDeferredData, isLoading: isReviewMenuLoading} = useReviewMenuData(
    pendingCommentData.pendingReviewIDs,
    {enabled: deferredDataEnabled},
  )

  const {viewerHasViolatedPushPolicy, viewerIsCopilotAttributed} = deferredDataEnabled ? deferredData : pullRequest
  const {viewerPermission} = deferredDataEnabled ? deferredData : repository

  const viewerCanWriteToRepo = viewerPermission === 'WRITE' || viewerPermission === 'ADMIN'

  const {persistedReview, persistReviewToStorage, removePersistedReviewFromStorage} = usePersistedReview(pathName)

  const totalPendingComments = pendingCommentData.pendingReviewIDs?.length ?? 0

  // We will probably want to use CurrentUserContext and useCurrentUser
  const viewerIsAuthor = author?.login === currentUserLogin
  const isPROpen = isPullRequestOpenState(state)
  const [reviewSubmissionState, setReviewSubmissionState] = useState<'idle' | 'submitting' | 'canceling'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | undefined>()
  const [reviewEvent, setReviewEvent] = useState<ReviewEvent>(persistedReview?.event ?? ReviewEvent.comment)

  const hasPendingReviews = (pendingCommentData?.pendingReviewIDs ?? []).length > 0

  // ref and state to keep track of the review body without causing re-renders
  // we ultimately only care about the final value when submitting the review and validating review body presence
  const initialReviewBody = persistedReview?.text ?? ''
  const reviewBodyRef = useRef<string>(initialReviewBody)
  const [hasReviewBody, setHasReviewBody] = useState<boolean>(!!persistedReview?.text)

  const submitDisabled =
    isReviewMenuLoading ||
    reviewSubmissionState === 'submitting' ||
    (!hasReviewBody && reviewEvent === ReviewEvent.comment && !totalPendingComments)

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const onReviewBodyChange = useCallback(
    (body: string) => {
      reviewBodyRef.current = body
      setHasReviewBody(!!body.trim())
      persistReviewToStorage(reviewEvent, body)
    },
    [persistReviewToStorage, reviewEvent, setHasReviewBody],
  )

  const handleReviewEventChange = useCallback(
    (newReviewEvent: string | null) => {
      if (Object.values(ReviewEvent).includes(newReviewEvent as ReviewEvent)) {
        setReviewEvent(newReviewEvent as ReviewEvent)
        persistReviewToStorage(newReviewEvent as ReviewEvent, reviewBodyRef.current)
      }
    },
    [persistReviewToStorage, setReviewEvent],
  )

  const handleInlineSuccess = useCallback(
    (redirectUrl: string) => {
      onOverlayModeChange('closed')

      // The component stays mounted so we must reset all transient state back to idle.
      setReviewSubmissionState('idle')
      setErrorMessage(undefined)
      setReviewEvent(ReviewEvent.comment)
      setHasReviewBody(false)
      reviewBodyRef.current = ''

      // Scroll/focus the new review timeline item. The item may not be in
      // the DOM yet since it arrives via live update.
      try {
        const hash = new URL(redirectUrl, window.location.origin).hash
        if (hash) {
          updateUrlHash(hash)
          scrollToElementWhenAvailable(hash.slice(1))
        }
      } catch {
        // Malformed redirectUrl — skip hash update and scroll
      }
    },
    [onOverlayModeChange, setReviewSubmissionState, setErrorMessage, setReviewEvent, setHasReviewBody],
  )

  const {mutate: submitReview} = useSubmitReviewMutation({
    onSuccess: ({redirectUrl}: ReviewResponse) => {
      removePersistedReviewFromStorage()

      if (redirectOnMutation) {
        window.location.href = redirectUrl
      } else {
        handleInlineSuccess(redirectUrl)
      }
    },
    onError: (error: Error) => {
      setReviewSubmissionState('idle')

      // TODO: add better error handling responses here based on message from server
      setErrorMessage(error.message)
    },
  })

  const {mutate: abandonReview} = useAbandonReviewMutation({
    onSuccess: ({redirectUrl}: ReviewResponse) => {
      removePersistedReviewFromStorage()

      if (redirectOnMutation) {
        window.location.href = redirectUrl
      } else {
        handleInlineSuccess(redirectUrl)
      }
    },
    onError: (error: Error) => {
      setReviewSubmissionState('idle')

      // TODO: add better error handling responses here based on message from server
      setErrorMessage(error.message)
    },
  })

  const handleReviewSubmit = useCallback(() => {
    if (submitDisabled) return

    if (errorMessage) setErrorMessage(undefined)
    setReviewSubmissionState('submitting')
    sendPullRequestAnalyticsEvent('submit_review_dialog.submit', 'SUBMIT_REVIEW_BUTTON')
    submitReview({
      body: reviewBodyRef.current,
      event: reviewEvent,
      headSha: headRefOid,
    })
  }, [
    errorMessage,
    headRefOid,
    reviewEvent,
    sendPullRequestAnalyticsEvent,
    setErrorMessage,
    setReviewSubmissionState,
    submitDisabled,
    submitReview,
  ])

  const handleReviewCancel = useCallback(() => {
    if (!pendingCommentData?.pendingReviewIDs || pendingCommentData?.pendingReviewIDs.length === 0) return
    if (!confirm('Are you sure you want to cancel? You will lose all your pending comments.')) return

    if (errorMessage) setErrorMessage(undefined)

    setReviewSubmissionState('canceling')
    sendPullRequestAnalyticsEvent('submit_review_dialog.cancel', 'CANCEL_REVIEW_BUTTON')
    abandonReview()
  }, [
    abandonReview,
    errorMessage,
    pendingCommentData.pendingReviewIDs,
    sendPullRequestAnalyticsEvent,
    setErrorMessage,
    setReviewSubmissionState,
  ])

  const reviewMode = getAllowedReviewMode({
    isPROpen,
    viewerAllowedNonCommentReviewTypes,
    viewerIsAuthor,
  })
  const title = `Finish your ${reviewMode}`

  const handleCloseReviewDialog = useCallback(() => onOverlayModeChange('closed'), [onOverlayModeChange])

  const handleNavigateToDiffComment = handleCloseReviewDialog

  const ReviewMenuBody = useMemo(
    () => (
      <ReviewMenuContent
        deferredDataEnabled={deferredDataEnabled}
        pendingReviewIds={pendingCommentData.pendingReviewIDs}
        initialReviewBody={initialReviewBody}
        isPROpen={isPROpen}
        onReviewBodyChange={onReviewBodyChange}
        reviewEvent={reviewEvent}
        viewerAllowedNonCommentReviewTypes={viewerAllowedNonCommentReviewTypes}
        viewerCanWriteToRepo={viewerCanWriteToRepo}
        viewerHasViolatedPushPolicy={viewerHasViolatedPushPolicy}
        viewerIsAuthor={viewerIsAuthor}
        viewerIsCopilotAttributed={viewerIsCopilotAttributed}
        handleNavigateToDiffComment={handleNavigateToDiffComment}
        handleReviewSubmit={handleReviewSubmit}
        handleReviewEventChange={handleReviewEventChange}
        stackBaseBranch={stackBaseBranch}
      />
    ),
    [
      deferredDataEnabled,
      pendingCommentData.pendingReviewIDs,
      initialReviewBody,
      isPROpen,
      onReviewBodyChange,
      reviewEvent,
      viewerAllowedNonCommentReviewTypes,
      viewerCanWriteToRepo,
      viewerHasViolatedPushPolicy,
      viewerIsAuthor,
      viewerIsCopilotAttributed,
      handleNavigateToDiffComment,
      handleReviewSubmit,
      handleReviewEventChange,
      stackBaseBranch,
    ],
  )

  const reviewMenuLoading = (
    <Stack align="center" justify="center" className="py-6">
      <Spinner size="medium" />
    </Stack>
  )

  const reviewMenuBody = isReviewMenuLoading ? reviewMenuLoading : ReviewMenuBody

  const reviewMenuFooter = (
    <ReviewMenuFooter
      errorMessage={errorMessage}
      hasPendingReviews={hasPendingReviews}
      reviewSubmissionState={reviewSubmissionState}
      handleReviewCancel={handleReviewCancel}
      handleCloseReviewDialog={handleCloseReviewDialog}
      handleReviewSubmit={handleReviewSubmit}
      submitDisabled={submitDisabled}
      reviewMode={reviewMode}
    />
  )

  return (
    <>
      {overlayPortal === 'internal' && <OverlayPortalContainer portalName={REVIEW_MENU_PORTAL_ROOT} />}

      <AnchoredOverlay
        open={overlayMode === 'anchored'}
        anchorRef={anchorRef}
        width="xlarge"
        onClose={handleCloseReviewDialog}
        displayCloseButton={false}
        variant={{regular: 'anchored', narrow: 'fullscreen'}}
        renderAnchor={null}
        focusZoneSettings={{disabled: true}}
        overlayProps={{
          role: 'dialog',
          'aria-labelledby': 'anchored-review-title',
          'aria-modal': 'true',
          ...(overlayPortal === 'internal' && {portalContainerName: REVIEW_MENU_PORTAL_ROOT}),
        }}
      >
        <Stack
          direction="horizontal"
          align="center"
          justify="space-between"
          className="py-2 border-bottom tmp-pl-3 pr-2"
        >
          <Heading as="h1" className="flex-1 f5" variant="small" id="anchored-review-title">
            {title}
          </Heading>
          <Stack direction="horizontal" align="center" gap="none">
            {hasPendingReviews && (
              <IconButton
                icon={MaximizeIcon}
                className={styles.ExpandSection}
                variant="invisible"
                aria-label="Expand this section"
                onClick={() => onOverlayModeChange('dialog')}
              />
            )}
            <IconButton icon={XIcon} variant="invisible" aria-label="Close" onClick={handleCloseReviewDialog} />
          </Stack>
        </Stack>

        <div className={styles.AnchoredReviewBody}>{reviewMenuBody}</div>

        <div className="tmp-p-3 border-top">{reviewMenuFooter}</div>
      </AnchoredOverlay>

      {overlayMode === 'dialog' && (
        <Dialog
          onClose={handleCloseReviewDialog}
          returnFocusRef={anchorRef}
          aria-label="Review changes"
          position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
          title={title}
          renderFooter={() => <Dialog.Footer>{reviewMenuFooter}</Dialog.Footer>}
        >
          {reviewMenuBody}
        </Dialog>
      )}
    </>
  )
}
